import type { EventqDetectionBox } from '../primitives/detection-bounding-box/detection-bounding-box.types';

export type EventqVideoObjectFit = 'contain' | 'cover';

/** Container + intrinsic video dimensions used to map detection coordinates. */
export interface EventqVideoContentLayout {
  containerWidth: number;
  containerHeight: number;
  videoWidth: number;
  videoHeight: number;
  objectFit: EventqVideoObjectFit;
}

/** Percent-based rectangle relative to a stage (0–100). */
export interface EventqPercentRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EventqVideoRenderMetrics {
  offsetX: number;
  offsetY: number;
  renderedWidth: number;
  renderedHeight: number;
}

function roundPercent(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** Pixel rect of the rendered video frame inside its container (object-fit contain/cover). */
export function computeVideoRenderMetrics(
  layout: EventqVideoContentLayout,
): EventqVideoRenderMetrics {
  const { containerWidth: cw, containerHeight: ch, videoWidth: vw, videoHeight: vh, objectFit } =
    layout;

  if (cw <= 0 || ch <= 0 || vw <= 0 || vh <= 0) {
    return {
      offsetX: 0,
      offsetY: 0,
      renderedWidth: Math.max(cw, 0),
      renderedHeight: Math.max(ch, 0),
    };
  }

  const scale =
    objectFit === 'contain' ? Math.min(cw / vw, ch / vh) : Math.max(cw / vw, ch / vh);
  const renderedWidth = vw * scale;
  const renderedHeight = vh * scale;

  return {
    offsetX: (cw - renderedWidth) / 2,
    offsetY: (ch - renderedHeight) / 2,
    renderedWidth,
    renderedHeight,
  };
}

/** Map a box defined in video-frame percent space to container percent space. */
export function mapVideoBoxToContainerPercent(
  box: EventqPercentRect,
  layout: EventqVideoContentLayout,
): EventqPercentRect {
  const { containerWidth: cw, containerHeight: ch } = layout;
  if (cw <= 0 || ch <= 0) {
    return box;
  }

  const { offsetX, offsetY, renderedWidth, renderedHeight } = computeVideoRenderMetrics(layout);
  const left = offsetX + (box.x / 100) * renderedWidth;
  const top = offsetY + (box.y / 100) * renderedHeight;
  const width = (box.width / 100) * renderedWidth;
  const height = (box.height / 100) * renderedHeight;

  return {
    x: roundPercent((left / cw) * 100),
    y: roundPercent((top / ch) * 100),
    width: roundPercent((width / cw) * 100),
    height: roundPercent((height / ch) * 100),
  };
}

/** Map detection boxes from video-frame coordinates to overlay stage coordinates. */
export function mapDetectionBoxesToContainer<T extends EventqPercentRect>(
  boxes: T[],
  layout: EventqVideoContentLayout | null | undefined,
): T[] {
  if (!layout) {
    return boxes;
  }

  return boxes.map((box) => ({
    ...box,
    ...mapVideoBoxToContainerPercent(box, layout),
  }));
}
